import {noop} from '@benchling/js-utils';
import {css, StyleSheet} from 'aphrodite';
import $ from 'jquery';
import React, {useEffect, useState, useRef} from 'react';

import {assert} from 'Asserts';
import ModelTypeConstants from 'constants/ModelTypeConstants';
import ConnectionFilesTable, {
  getFiltersFromConnectionIds,
  useConnectionIdsAndFiltersState,
} from 'instruments/connection-files/ConnectionFilesTable';
import SearchInput from 'search/inputs/SearchInput';
import {Filter} from 'search/SearchConstants';
import InlineBanner, {InlineBannerType} from 'ui-library/banners/InlineBanner';
import FilesPickerComponent from 'ui-library/FilesPickerComponent';
import FieldLabel from 'ui-library/inputs/FieldLabel';
import Modal from 'ui-library/modals/Modal';
import {Tab, TabContainer} from 'ui-library/TabContainer';
import * as logging from 'util/Logging';
import removeNulls from 'util/removeNulls';

import {FileListingGraphQL} from '../../../graphql-fragments/files/__generated/FileListingGraphQL';

interface SelectPipelineFilesModalProps {
  onRequestSubmit: () => void;
  onRequestClose: () => void;
  title: string;
  fileIds: Array<string>;
  isMulti: boolean;
  minItems?: number;
  maxItems?: number;
  onSelectFiles: (newFileIds: Array<string>) => void;
  onUploadFiles: (files: Array<File>, selectedFileIds?: Array<string>) => JQueryPromise<void>;
  defaultFilters?: Array<Filter>;
}

interface ConnectionFilesTabProps extends SelectPipelineFilesModalProps {
  selectedFileIds: Array<string>;
  setSelectedFileIds: (selectedFileIds: Array<string>) => void;
}

enum TabType {
  DATA_CATALOG = 'data_catalog',
  UPLOAD = 'upload',
  CONNECTION = 'connection',
}

function getInitiallySelectedRowIndices(
  connectionFiles: Array<FileListingGraphQL>,
  selectedFileIds: Array<string>
): Set<number> {
  return new Set(
    removeNulls(connectionFiles.map((file, idx) => (selectedFileIds.includes(file.id) ? idx : null)))
  );
}

function ConnectionFilesTab({
  isMulti,
  maxItems,
  selectedFileIds,
  setSelectedFileIds,
  defaultFilters,
}: ConnectionFilesTabProps): JSX.Element {
  const {
    connectionIdsState: [selectedConnectionIds, setSelectedConnectionIds],
    filtersState: [filters, setFilters], // State management for ConnectionFilesTable
  } = useConnectionIdsAndFiltersState({
    initialFilters: defaultFilters ?? getFiltersFromConnectionIds([]),
    initialConnectionIds: null,
  });
  // State management for ConnectionFilesTable
  const [connectionFiles, setConnectionFiles] = useState<Array<FileListingGraphQL>>([]);
  const [selectedRowIndices, setSelectedRowIndices] = useState<Set<number>>(
    getInitiallySelectedRowIndices(connectionFiles, selectedFileIds)
  );

  // Ensure that files that have already been selected and are present in the ConnectionFilesTable are
  // selected when the table loads.
  useEffect(() => {
    setSelectedRowIndices(getInitiallySelectedRowIndices(connectionFiles, selectedFileIds));
  }, [connectionFiles, selectedFileIds]);

  const hasMaxItems = maxItems != null && selectedFileIds.length >= maxItems;

  // Disable "select all" if clicking it would select more connection files than maxItems. Clicking on the
  // checkbox only "selects" if no row indices are selected yet. Otherwise, clicking on it gets rid of all
  // current selections.
  const isSelectAllDisabled =
    selectedRowIndices.size === 0 &&
    maxItems != null &&
    selectedFileIds.length + connectionFiles.length > maxItems;
  // If hasMaxItems === true, then only allow the user to de-select by disabling all *unselected* rows
  const isDisabledMap: Array<boolean> | undefined = hasMaxItems
    ? connectionFiles.map((file, rowIndex) => !selectedRowIndices.has(rowIndex))
    : undefined;

  function handleSetRowIndices(newIndices: Set<number>): void {
    // Set the selection state of the ConnectionFilesTable
    const newSelectedRowIndices = new Set(selectedRowIndices);
    for (const rowIndex of newIndices) {
      if (newSelectedRowIndices.has(rowIndex)) {
        // Selected--> unselected
        newSelectedRowIndices.delete(rowIndex);
      } else {
        // Unselected --> selected
        newSelectedRowIndices.add(rowIndex);
      }
    }
    setSelectedRowIndices(newSelectedRowIndices);

    const newSelectedConnectionFileIds = [...newSelectedRowIndices].map(
      (rowIndex) => connectionFiles[rowIndex].id
    );
    if (isMulti) {
      // Inspect selectedFileIds for ids that should not be affected by the change in row indices. Specifically,
      // we don't want to remove files in selectedFileIds that aren't configurable via ConnectionFilesTable,
      // and we also don't want to needlessly change the order of selectedFileIds.
      const allConnectionFileIds = connectionFiles.map(({id}) => id);
      const stableSelectedFileIds = selectedFileIds.filter(
        (id) => !allConnectionFileIds.includes(id) || newSelectedConnectionFileIds.includes(id)
      );
      setSelectedFileIds([
        ...stableSelectedFileIds,
        ...newSelectedConnectionFileIds.filter((id) => !stableSelectedFileIds.includes(id)),
      ]);
    } else {
      assert(
        newSelectedConnectionFileIds.length <= 1,
        'User was able to select multiple connection files for an isMulti=false file variable.'
      );
      setSelectedFileIds(newSelectedConnectionFileIds);
    }
  }

  return (
    <div className={css(styles.connectionTabBody)}>
      <div>
        <FieldLabel label="Connections to filter by" />
        <SearchInput
          autoFocus={false}
          types={[ModelTypeConstants.CONNECTION]}
          selected={selectedConnectionIds}
          onSelect={(connectionId) => {
            if (connectionId == null) {
              return;
            }
            setSelectedConnectionIds([...selectedConnectionIds, connectionId]);
          }}
          onRemove={(connectionId) => {
            if (connectionId == null) {
              return;
            }
            setSelectedConnectionIds(selectedConnectionIds.filter((id) => id !== connectionId));
          }}
          placeholder="Search for connections..."
          isMulti={isMulti}
        />
      </div>
      <div>
        <FieldLabel label="Connection files" />
        <ConnectionFilesTable
          isSelectAllDisabled={isSelectAllDisabled}
          isDisabledMap={isDisabledMap}
          hideCheckboxColumn={false}
          allowMultiple={isMulti}
          connectionFiles={connectionFiles}
          setConnectionFiles={setConnectionFiles}
          columns={['File name', 'Status', 'Modified at', 'Created at']}
          filters={filters}
          setFilters={setFilters}
          selectedRowIndices={selectedRowIndices}
          onSelectRows={handleSetRowIndices}
        />
      </div>
    </div>
  );
}

export default function SelectPipelineFilesModal(props: SelectPipelineFilesModalProps): JSX.Element {
  const {title, fileIds, onSelectFiles, onRequestClose, isMulti, maxItems, onUploadFiles} = props;
  const [activeTab, setActiveTab] = useState<TabType>(TabType.CONNECTION);
  const [selectedFileIds, setSelectedFileIds] = useState<Array<string>>(fileIds);

  const fileInputRef = useRef<FilesPickerComponent>(null);
  const handleSubmit = (): void => {
    if (activeTab === TabType.UPLOAD) {
      // If tab is UPLOAD, then use the FilesPickerComponent's ref to handle the upload then close the modal
      assert(fileInputRef.current != null, 'Expected non-null fileInputRef.current');
      fileInputRef.current.handleUpload({allComplete: onRequestClose, uploadComplete: noop});
    } else {
      // Otherwise just set the file IDs and close
      onSelectFiles(selectedFileIds);
      onRequestClose();
    }
  };

  const hasMaxItems = maxItems != null && selectedFileIds.length >= maxItems;
  const maxFilesWarningBanner = (
    <InlineBanner
      bannerType={InlineBannerType.WARNING}
      supportingInfo={`Maximum file limit of ${maxItems} has been reached. Files must be removed before any new files can be added.`}
    />
  );

  return (
    <Modal
      // The select from connection UI is really cramped unless we expand the modal
      large={activeTab === TabType.CONNECTION}
      enableRedesign={true}
      title={title}
      onRequestClose={onRequestClose}
      onRequestSubmit={handleSubmit}
      buttons={[
        {text: 'Cancel', cancel: true},
        {
          text: 'Select',
          submit: true,
          disabled: false,
        },
      ]}
    >
      <TabContainer<TabType> activeTab={activeTab} onRequestTabChange={setActiveTab}>
        <Tab tabKey={TabType.CONNECTION} title="Select from connection">
          <div className={css(styles.tabBody)}>
            {hasMaxItems && maxFilesWarningBanner}
            <ConnectionFilesTab
              {...props}
              selectedFileIds={selectedFileIds}
              setSelectedFileIds={setSelectedFileIds}
            />
          </div>
        </Tab>
        <Tab tabKey={TabType.DATA_CATALOG} title="Select from data catalog">
          <div className={css(styles.tabBody)}>
            {hasMaxItems && maxFilesWarningBanner}
            <SearchInput
              autoFocus={false}
              disableTyping={hasMaxItems || (!isMulti && selectedFileIds.length === 1)}
              maxItems={maxItems}
              types={[ModelTypeConstants.FILE]}
              selected={selectedFileIds}
              onSelect={(fileId) => {
                if (fileId == null) {
                  return;
                }
                if (isMulti) {
                  // Do nothing if the number of selected files is already >= maxItems, and log an error so
                  // that we know that something is wrong with our UI.
                  if (hasMaxItems) {
                    logging.error(
                      'User was able to select files past maxItems limit from "Select from data catalog" tab.'
                    );
                    return;
                  }
                  setSelectedFileIds([...selectedFileIds, fileId]);
                } else {
                  setSelectedFileIds([fileId]);
                }
              }}
              onRemove={(fileId) => {
                if (fileId == null) {
                  return;
                }
                setSelectedFileIds(selectedFileIds.filter((id) => id !== fileId));
              }}
              placeholder={hasMaxItems ? '' : 'Search for files...'}
              isMulti={isMulti}
            />
          </div>
        </Tab>
        <Tab tabKey={TabType.UPLOAD} title={`Upload file${isMulti ? 's' : ''}`}>
          <div className={css(styles.tabBody)}>
            {hasMaxItems && maxFilesWarningBanner}
            <FilesPickerComponent
              isDisabled={hasMaxItems}
              multiple={isMulti}
              // Calculate the max files that a user can select for upload based on how many files have already
              // been selected.
              maxFiles={maxItems == null ? undefined : maxItems - selectedFileIds.length}
              upload={(files: Array<File>) => {
                // Do nothing if the number of selected files is already >= maxItems, and log an error so
                // that we know that something is wrong with our UI.
                if (hasMaxItems) {
                  logging.error('User was able to select files past maxItems limit from "Upload files" tab.');
                  return $.when();
                }
                return onUploadFiles(
                  files,
                  // If isMulti=true, then the new file(s) will be appended to the list of already selected
                  // files. Otherwise, the existing file will be overwritten by the new file.
                  isMulti ? selectedFileIds : []
                );
              }}
              bulkUpload={true}
              ref={fileInputRef}
            />
          </div>
        </Tab>
      </TabContainer>
    </Modal>
  );
}

const styles = StyleSheet.create({
  tabBody: {
    display: 'flex',
    flexDirection: 'column',
    gap: 8,
  },
  connectionTabBody: {
    display: 'flex',
    flexDirection: 'column',
    gap: 16,
  },
});
